import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { RouteFallback } from '@/components/RouteFallback';
import { integrationStatusRoutes } from '@/features/integration-status/routes';
import { oemRoutes } from '@/features/oem/routes';
import { settingsRoutes } from '@/features/settings/routes';
import { tenantsRoutes } from '@/features/tenants/routes';

// Route-level code splitting per docs/web/conventions.md §1.5 — every page
// component is lazy-imported so the initial JS bundle stays inside the
// 250 KB gzipped budget (ADR-0005 §12).
const App = lazy(() => import('@/App'));
const AdminLayout = lazy(() => import('@/components/layout/AdminLayout'));
const LoginPage = lazy(() => import('@/auth/login/LoginPage'));
const TotpChallenge = lazy(() => import('@/auth/mfa/TotpChallenge'));
const RecoveryCodeChallenge = lazy(() => import('@/auth/mfa/RecoveryCodeChallenge'));
const VerifyEmailPage = lazy(() => import('@/auth/verify-email/VerifyEmailPage'));
const SignupPage = lazy(() => import('@/auth/signup/SignupPage'));
const RequestResetPage = lazy(() => import('@/auth/password/RequestResetPage'));
const ConfirmResetPage = lazy(() => import('@/auth/password/ConfirmResetPage'));
const AcceptInvitePage = lazy(() => import('@/auth/invite/AcceptInvitePage'));
const EnrollmentRequiredPage = lazy(() => import('@/auth/mfa/EnrollmentRequiredPage'));

function withSuspense(node: ReactNode): ReactNode {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/mfa/totp',
    element: withSuspense(<TotpChallenge />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/mfa/recovery',
    element: withSuspense(<RecoveryCodeChallenge />),
    errorElement: <RouteErrorBoundary />,
  },
  // Tenant-slug-prefixed auth-tail routes — slug is URL-driven, surfaced to
  // the user, and rides the wire on submit (signup, reset-request).
  // verify-email is token-only at the backend but the slug stays in the URL
  // so the user sees which clinic they're verifying for.
  {
    path: '/:tenantSlug/auth/verify-email',
    element: withSuspense(<VerifyEmailPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/:tenantSlug/auth/signup',
    element: withSuspense(<SignupPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/:tenantSlug/auth/password/reset-request',
    element: withSuspense(<RequestResetPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // HCP-108 forced-MFA-enrollment surface (ADR-0007). The slug is URL-driven
  // for symmetry with the other tenant-slug-prefixed auth routes; the pending
  // enrollment-token (location state) carries the actual tenant context on
  // the wire. Defense-in-depth lives on the backend `EnrollmentPendingGuard`
  // — the route itself is just the SPA landing surface.
  {
    path: '/:tenantSlug/auth/mfa-enrollment',
    element: withSuspense(<EnrollmentRequiredPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Token-only auth-tail routes — tenant comes from the JWT claim on the
  // single-use token, not from the URL.
  {
    path: '/auth/password/reset-confirm',
    element: withSuspense(<ConfirmResetPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/auth/invite/accept',
    element: withSuspense(<AcceptInvitePage />),
    errorElement: <RouteErrorBoundary />,
  },
  ...settingsRoutes,
  // Admin app: one layout with the ability-driven nav and the
  // access banner. Feature fragments nest under it.
  {
    path: '/',
    element: withSuspense(
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>,
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: withSuspense(<App />) },
      ...tenantsRoutes,
      ...oemRoutes,
      ...integrationStatusRoutes,
    ],
  },
]);
